import { Component, forwardRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';

import { AbstractFormFieldComponent } from '../../../PageBuilder/components/FormField/abstract-form-field.component';
import { DateRangeFieldModel } from '../../models/date-range-field.model';
import { DateQueryModel } from '../../../Forms/models/date-query.model';
import { ReportSeriesModel } from '../../models/report-series.model';
import { ReportDateRangeModel } from '../../models/report-date-range.model';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import {
    getMonitoringDateQueryInputFolderOptions,
    getMonitoringDateQueryInputTopicOptions,
} from '../../../../Monitor/Shared/models/monitoring-date-query-input-options';
import { filter, Subscription } from 'rxjs';
import { SearchRangeModel } from '../../../../Monitor/Core/models/search-range.model';
import { MonitorConfigService } from '../../../MonitorShared/services/monitor-config.service';
import { dateQueryInput24HoursOption } from '../../../Forms/models/date-query-input-options.model';
import { GoogleAnalyticsChartTypeEnum } from '../../../../api/externalData/enums/google-analytics-chart-types.enum';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { ReportBlockTypeEnum } from '../../enums/report-block-type.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { DateQueryPopupComponent } from '../../../Forms/components/DateQueryPopup/date-query-popup.component';
import { defaultDateTypeOptions } from '../../../Forms/components/DateRangeSelector/date-range-selector.component';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';


@Component({
    selector: 'agpr-date-range-field-no-series',
    templateUrl: './date-range-field-no-series.component.html',
    styleUrls: ['./date-range-field-no-series.component.scss'],
    providers: [
        {
            provide: AbstractFormFieldComponent,
            useExisting: forwardRef(() => DateRangeFieldNoSeriesComponent)
        }
    ],
    standalone: false
})
export class DateRangeFieldNoSeriesComponent extends AbstractFormFieldComponent implements OnDestroy, OnChanges {
    @Input() declare field: DateRangeFieldModel;
    @Input() declare value: DateQueryModel;
    @Input() dateTypeOptions:  DropdownOptionModel[] = defaultDateTypeOptions;

    @ViewChild('dateQueryPopup') dateQueryPopup: DateQueryPopupComponent;

    dateFilterOptions: DropdownOptionModel[] = getMonitoringDateQueryInputFolderOptions();
    hideDateTypeSelection: boolean = false;

    dateRange: DateQueryModel;
    showAllOption: boolean = false;

    searchRange: SearchRangeModel = null;
    gaChartType = GoogleAnalyticsChartTypeEnum;

    private preferences: PreferencesModel;
    readonly googleAnalyticsChartTypes: string[] = [
        ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS, ReportBlockTypeEnum.TOP_REFERRAL_SOURCES, ReportBlockTypeEnum.REFERRAL_TREND
    ];
    subscriptions: Subscription[] = [];

    constructor(
        private monitorConfigService: MonitorConfigService,
        public currentUserStateService: CurrentUserStateService
    ) {
        super();
        this.subscriptions.push(
            this.monitorConfigService.getSearchRange().subscribe(
                searchRange => {
                    this.searchRange = searchRange;
                    this.setMinimumSearchRange();
                }
            ),
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(prefs => {
                this.preferences = prefs;
            })
        );
        this.hideDateTypeSelection = this.googleAnalyticsChartTypes.includes(this.block?.type?.key);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.hasOwnProperty('value') || changes.hasOwnProperty('dateTypeOptions')) {
            if (this.value) {
                // if date range dropdown options do not contain the initial date range, setting it to dateQueryInput24HoursOption
                if (this.value?.type === DateQueryTypeEnum.RELATIVE
                    && !this.dateFilterOptions.find(option => {
                        return option.value.query.relativeStartOffsetH === this.value.relativeStartOffsetH;
                    })) {
                    this.value = new ReportDateRangeModel().setFromDateQuery(dateQueryInput24HoursOption.value.query);
                }

                this.dateRange = this.value;
            }


            this.setShowAllOption(this.showAllOption);
            this.dateTypeOptions = defaultDateTypeOptions;

            this.setMinimumSearchRange();
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }

    /**
     * Updates the date range.
     *
     * @param dateRange
     *
     * @returns void
     */
    dateRangeChanged(): void {
        this.value = this.dateRange;

        this.changes.emit(this.value);
    }

    /**
     * Sets the date options based on the seriesType
     */
    setShowAllOption(showAll: boolean) {
        if (showAll) {
            this.dateFilterOptions = getMonitoringDateQueryInputFolderOptions();
        } else {
            this.dateFilterOptions = getMonitoringDateQueryInputTopicOptions();
        }

        this.showAllOption = showAll;
    }

    /**
     * Returns a new ReportSeriesModel[] with default values
     *
     * @returns ReportSeriesModel[]
     */
    getDefaultSeriesList(): ReportSeriesModel[] {
        let blockTypeKey = null;

        if (this.block && this.block.type && this.block.type.key) {
            blockTypeKey = this.block.type.key;
        }

        return [new ReportSeriesModel(null, blockTypeKey)];
    }

    /**
     * If the appropriate data is set, make sure that topics don't go outside their allowed range
     */
    private setMinimumSearchRange(): void {
        if (!this.searchRange ||
            // skip it if we use saved source
            this.showAllOption ||
            // skip it if we use ga charts
            (this.block && this.gaChartType[this.block.type.key])
        ) {
            return;
        }

        const dateRange = this.value;

        if (dateRange?.absoluteRange) {
            if (dateRange.absoluteRange.end < this.searchRange.from) {
                dateRange.absoluteRange = null;
                dateRange.absoluteDate = this.searchRange.from;
            } else if (dateRange.absoluteRange.start < this.searchRange.from) {
                dateRange.absoluteRange.start = this.searchRange.from;
            }
        }

        if (dateRange?.absoluteDate && dateRange?.absoluteDate < this.searchRange.from) {
            dateRange.absoluteDate = this.searchRange.from;
        }

        // Force it so that the displayValue gets updated
        this.dateQueryPopup?.writeValue(this.dateRange);

    }
}
